import * as mediaQuery from "components/MediaQuery";
import { ProfileAccountInfo } from "api/interfaces/accountInterface.interface";
import { CVVPairItem } from "api/interfaces/reportInterface.interface";
import RBACWrapper from "components/blocks/function/RBACWrapper";
import { Text, UnderlineText } from "components/atoms/text/Text";
import { COLORS } from "styles/colors";
import { useAppSelector } from "redux/hooks";
import styled from "styled-components";
import { FLEX } from "styles/flex";
import { CVVType } from "./interface";
import Spinner from "components/atoms/loader/Spinner";
import { useState } from "react";
import { getAccountTimeFormatDate, getTimezoneAbbr } from "utils/timeUtil";

type CvvCameraThumbnailEventInfoProps = {
  pairItem: CVVPairItem;
  onClickCheckNow: () => Promise<void>;
  timezone: string;
  disabledCheckNow: boolean;
  isProtected: boolean;
};

export function CvvCameraThumbnailEventInfo({
  pairItem,
  onClickCheckNow,
  timezone,
  disabledCheckNow,
  isProtected,
}: CvvCameraThumbnailEventInfoProps) {
  const selectedAccount: ProfileAccountInfo = useAppSelector(
    (state) => state.accountSettings
  );

  // CVV의 시간은 타임존이 적용된 시간이므로 Offset으로 시간이동을 시키면 안됨.
  const thumbnailTimestamp =
    pairItem?.eventThumbnail?.date && timezone
      ? `${getAccountTimeFormatDate(
          pairItem.eventThumbnail.date,
          { ...selectedAccount, timezone: "UTC" },
          true,
          true
        ).replace(" GMT", "")} ${getTimezoneAbbr(timezone)}`
      : "";

  return (
    <>
      <mediaQuery.Default>
        <div>
          <ThumbnailTimestamp value={thumbnailTimestamp} />
          <DefaultTitle>
            {!isProtected && (
              <CheckNowBtn
                onClick={onClickCheckNow}
                disabledCheckNow={disabledCheckNow}
                data-testid="reports-view-cvv-check-now-btn-click-check-now"
              />
            )}
          </DefaultTitle>
        </div>
      </mediaQuery.Default>

      <mediaQuery.Mobile>
        <MobileTitle>
          <span></span>
          <ThumbnailTimestamp value={thumbnailTimestamp} />
          {!isProtected && (
            <CheckNowBtn
              onClick={onClickCheckNow}
              disabledCheckNow={disabledCheckNow}
              data-testid="reports-view-cvv-check-now-btn-click-check-now-2"
            />
          )}
        </MobileTitle>
      </mediaQuery.Mobile>
    </>
  );
}

type CvvCameraThumbnailReferenceInfoProps = {
  pairItem: CVVPairItem;
  dayOrNight: CVVType;
  onClickUpdate: () => Promise<void>;
  timezone: string;
  isProtected: boolean;
};

export function CvvCameraThumbnailReferenceInfo({
  pairItem,
  dayOrNight,
  onClickUpdate,
  timezone,
  isProtected,
}: CvvCameraThumbnailReferenceInfoProps) {
  const selectedAccount: ProfileAccountInfo = useAppSelector(
    (state) => state.accountSettings
  );

  // CVV의 시간은 타임존이 적용된 시간이므로 Offset으로 시간이동을 시키면 안됨.
  const thumbnailTimestamp =
    pairItem?.referenceThumbnail?.date && timezone
      ? `${getAccountTimeFormatDate(
          pairItem.referenceThumbnail.date,
          { ...selectedAccount, timezone: "UTC" },
          true,
          true
        ).replace(" GMT", "")} ${getTimezoneAbbr(timezone)}`
      : "";

  return (
    <>
      <mediaQuery.Default>
        <div>
          <ThumbnailTimestamp value={thumbnailTimestamp} />
          <DefaultTitle>
            <ReferenceNightOrDay dayOrNight={dayOrNight} />
            {!isProtected && <UpdateBtn onClickUpdate={onClickUpdate} />}
          </DefaultTitle>
        </div>
      </mediaQuery.Default>

      <mediaQuery.Mobile>
        <MobileTitle>
          <ReferenceNightOrDay dayOrNight={dayOrNight} />
          <ThumbnailTimestamp value={thumbnailTimestamp} />
          {!isProtected && <UpdateBtn onClickUpdate={onClickUpdate} />}
        </MobileTitle>
      </mediaQuery.Mobile>
    </>
  );
}

type ReferenceNightOrDayProps = {
  dayOrNight: CVVType;
};

const ReferenceNightOrDay = ({ dayOrNight }: ReferenceNightOrDayProps) => {
  if (dayOrNight === "night") {
    return (
      <Text color="#00A8FF" bold>
        Reference Night
      </Text>
    );
  }
  if (dayOrNight === "day") {
    return (
      <Text color="#FF9D64" bold>
        Reference Day
      </Text>
    );
  }
  return <></>;
};

type ThumbnailTimestampProps = {
  value: string;
};

const ThumbnailTimestamp = ({ value }: ThumbnailTimestampProps) => {
  return <Text className="timestamp">{value}</Text>;
};

type UpdateBtnProps = {
  onClickUpdate: () => Promise<void>;
};

const UpdateBtn = (props: UpdateBtnProps) => {
  const [loading, setLoading] = useState<boolean>(false);
  const onClick = async () => {
    setLoading(true);
    props
      .onClickUpdate()
      .then(() => {
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  };

  if (loading) {
    return <Spinner size={8} margin={2} inline={true} />;
  }
  return (
    <RBACWrapper requiredPermissions="manageReportRules">
      <UnderlineText
        color={COLORS.BUTTONS1}
        onClick={onClick}
        data-testid="reports-view-cvv-underline-text-click"
      >
        Update
      </UnderlineText>
    </RBACWrapper>
  );
};

type CheckNowBtnProps = {
  onClick: () => Promise<void>;
  disabledCheckNow: boolean;
};

const CheckNowBtn = (props: CheckNowBtnProps) => {
  const [loading, setLoading] = useState<boolean>(false);
  const onClick = async () => {
    if (props.disabledCheckNow) return;
    setLoading(true);
    props
      .onClick()
      .then(() => {
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  };
  if (loading) {
    return <Spinner size={8} margin={2} inline={true} />;
  }
  return (
    <RBACWrapper requiredPermissions="manageReportRules">
      <UnderlineText
        color={COLORS.BUTTONS1}
        onClick={onClick}
        style={{
          cursor: props.disabledCheckNow ? "not-allowed" : "pointer",
        }}
        data-testid="reports-view-cvv-underline-text-click-2"
      >
        Check Now
      </UnderlineText>
    </RBACWrapper>
  );
};

const DefaultTitle = styled.div`
  width: 100%;
  ${FLEX.FlexStartCenter};
  margin-top: 6px;
  gap: 10px;
`;

const MobileTitle = styled.div`
  position: relative;
  width: 100%;
  display: grid;
  grid-template-columns: 1fr 1fr;
  > :nth-child(2) {
    grid-column: 1 / 2;
  }
  > :nth-child(3) {
    grid-column: 2 / 3;
    ${FLEX.FlexEndCenter};
    text-align: right;
    flex-direction: row;
  }
`;
